import {
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  defined,
  EllipsoidTerrainProvider,
  Model,
  Cesium3DTileset,
  Cesium3DTileFeature,
  Entity,
  ModelGraphics,
} from "@cesium/engine";
import DitapViewer from "../basic/DitapViewer";
import AnalysisClipping from "../analysis/AnalysisClipping";
import PopupAnalysisClipping from "../popup/PopupAnalysisClipping";
import Utils from "../basic/Utils";
import DitapAlert from "../popup/DitapAlert";

/**
 * Interaction 클래스에서 지원하는 object 추출
 */
const getSelectObject = (selectFeature) => {
  let selectObject;
  if (defined(selectFeature)) {
    if (selectFeature instanceof Cesium3DTileFeature) {
      selectObject = selectFeature.tileset;
    } else if (selectFeature?.primitive instanceof Cesium3DTileset) {
      selectObject = selectFeature.primitive;
    } else if (selectFeature?.id instanceof Entity) {
      selectObject = selectFeature.id;
    } else if (selectFeature?.primitive?.id instanceof Entity) {
      selectObject = selectFeature.primitive.id;
    } else if (selectFeature?.primitive instanceof Model) {
      selectObject = selectFeature.primitive;
    }
  }

  return selectObject;
};

/**
 * 절단면 분석을 위한 사용자 입력(마우스/키보드) 클래스.</br>
 * Clipping plane analysis user input (mouse/keyboard) class.
 * @alias InteractionClipping
 *
 * @constructor
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
 *   Ditap.Cartesian3.fromDegrees(126.9782349646649, 37.56671536989183, 100)
 * );
 * const model = await Ditap.Model.fromGltfAsync{
 *  id: "model_01",
 *  url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
 *  modelMatrix: modelMatrix,
 *  scale: 3,
 * });
 * viewer.scene.primitives.add(model);
 *
 * // 1. 실행
 * const interactionClipping = new Ditap.InteractionClipping(viewer);
 * interactionClipping.on();
 */
class InteractionClipping {
  constructor(viewer) {
    this._handler = undefined; // 핸들러
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    } else {
      throw Error("InteractionClipping viewer type error");
    }

    this._analysisClipping = undefined;
    this._popupAnalysisClipping = undefined;

    this._isOn = false; // 핸들러 실행 여부
    this._mousePosition = undefined;
    this._infoLabelEntity = undefined;
    this._selectObject = undefined;

    this._keyDownEvent = undefined;

    this._buttonElement = undefined;
    this.cancelBtn = undefined;
    this._isMobile = undefined; // 모바일 여부
    this._instantAlert = undefined;
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  get viewer() {
    return this._viewer;
  }
  set viewer(viewer) {
    this._viewer = viewer;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  get mousePosition() {
    return this._mousePosition;
  }
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화 상태.</br>
   * Handler enabled or disabled.
   * @type {boolean}
   * @readonly
   */
  get isOn() {
    return this._isOn;
  }
  // set isOn(boolean) {
  //   this._isOn = boolean;
  // }

  /*
   * 키 다운 시 실행 이벤트.</br>
   * Event to execute when a key is pressed.
   * @type {Function}
   */
  get keyDownEvent() {
    return this._keyDownEvent;
  }
  set keyDownEvent(keyDownEvent) {
    this._keyDownEvent = keyDownEvent;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }

  /*
   * 절단면 분석 팝업 인스턴스.</br>
   * PopupAnalysisClipping instance
   * @type {PopupAnalysisClipping}
   */
  get popupAnalysisClipping() {
    return this._popupAnalysisClipping;
  }
  set popupAnalysisClipping(popupAnalysisClipping) {
    this._popupAnalysisClipping = popupAnalysisClipping;
  }

  /**
   * 선택 모델.</br>
   * Select model
   * @readonly
   * @type {Model | Cesium3DTileset}
   */
  get selectObject() {
    return this._selectObject;
  }
  set selectObject(selectObject) {
    if (
      selectObject instanceof Model ||
      selectObject instanceof Cesium3DTileset ||
      selectObject instanceof ModelGraphics
    ) {
      this._selectObject = selectObject;
    } else {
      this._selectObject = undefined;
    }
  }

  get analysisClipping() {
    return this._analysisClipping;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼.</br>
   * Button that activates the user input class.
   * @type {Element}
   */
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }
  get buttonElement() {
    return this._buttonElement;
  }

  /*
   * Terrain provider 여부에 따라 새로운 Cartesian 반환.</br>
   * Returns new cartesian based on ellipsoid terrain provider presence.
   * @param {Cartesian3} position 재구성할 Cartesian 좌표.</br> The cartesian to be reconstructed.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @return {Cartesian3} 재구성된 Cartesian 좌표. </br> Reconstructed based on ellipsoid terrain provider presence.
   */
  pickPosition(position, viewer) {
    let newPosition;
    let isEllipsoid = false;
    if (viewer.terrainProvider instanceof EllipsoidTerrainProvider) {
      isEllipsoid = true;
    }

    if (!isEllipsoid) {
      newPosition = viewer.scene.pickPosition(position);
    } else {
      newPosition = viewer.camera.pickEllipsoid(
        position,
        viewer.scene.globe.ellipsoid
      );
    }

    return newPosition;
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      // 마우스 좌표 설정
      const position = this.pickPosition(e.endPosition, viewer);
      if (defined(position)) {
        this.mousePosition = position;

        // 도움말 라벨 좌표 이동
        Utils.changeLabelLocation(
          viewer,
          this.mousePosition,
          this.infoLabelEntity
        );
      } else {
        this.infoLabelEntity.classList.add("hide");
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const viewer = this.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      const selectFeature = viewer.scene.pick(e.position);
      if (!this.selectObject && selectFeature) {
        const selectObject = getSelectObject(selectFeature);
        if (selectObject) {
          let radius;

          // ModelGraphic
          if (
            selectObject instanceof Entity &&
            selectFeature?.primitive instanceof Model
          ) {
            this.selectObject = selectObject.model;

            const boundingSphere = selectFeature.primitive.boundingSphere;
            radius = Math.ceil(boundingSphere.radius);

            // Cesium3DTileset
          } else if (selectObject instanceof Cesium3DTileset) {
            this.selectObject = selectObject;

            const boundingSphere = selectObject.boundingSphere;
            radius = Math.ceil(boundingSphere.radius);

            // Model
          } else if (selectObject instanceof Model) {
            this.selectObject = selectObject;

            const boundingSphere = selectObject.boundingSphere;
            radius = Math.ceil(boundingSphere.radius);
          } else {
            this._instantAlert.open();
          }

          this.mobileInfoLabel?.label.remove();
          this.infoLabel?.remove();

          this._analysisClipping = this.viewer.ditapStorage.getAnalysisClippingByObject(
            this.selectObject
          );
          if (!this._analysisClipping) {
            this._analysisClipping = new AnalysisClipping(
              this.viewer,
              this.selectObject
            );
          }

          this.popupAnalysisClipping = new PopupAnalysisClipping(
            this.analysisClipping,
            viewer.ditapPaneManager,
            {
              closeCallback: (popup) => {
                this.off();
                popup.destroy(false); // false 고정
              },
              radius: radius,
            }
          );
          // this.popupAnalysisClipping.setXMinValue(-radius);
          // this.popupAnalysisClipping.setXMaxValue(radius);
          // this.popupAnalysisClipping.setYMinValue(-radius);
          // this.popupAnalysisClipping.setYMaxValue(radius);
          // this.popupAnalysisClipping.setZMinValue(-radius);
          // this.popupAnalysisClipping.setZMaxValue(radius);
          // this.popupAnalysisClipping.setXSliderValue(
          //   this.popupAnalysisClipping.clippingRangeXCurrentValue.innerText || 0
          // );
          // this.popupAnalysisClipping.setYSliderValue(
          //   this.popupAnalysisClipping.clippingRangeYCurrentValue.innerText || 0
          // );
          // this.popupAnalysisClipping.setZSliderValue(
          //   this.popupAnalysisClipping.clippingRangeZCurrentValue.innerText || 0
          // );

          this.popupAnalysisClipping.open();

          // 팝업 닫기 버튼 => AnalysisClipiing 초기화
          // this.popupAnalysisClipping.closeBtn.addEventListener("click", () => {
          //   this.off();
          // });
        } else {
          this._instantAlert.open();
        }
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      if (this._isOn) {
        this.off();
      }
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    // ESC : 체적 측정 실행 취소
    if (event.key === "Escape") {
      this.off();
    }
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (this.keyDownEvent = (e) => {
        this.onKeyDown(e);
      })
    );
  }

  /**
   * 절단면 분석 핸들러 실행.</br>
   * Execute clipping analysis handler.
   */
  on() {
    if (!this._isOn) {
      this.viewer.currentEvent = this;
      const viewer = this.viewer;
      this._isMobile = Utils.detectDeviceType();

      // 버튼 활성화
      this.buttonElement?.classList.add("on");

      // 핸들러 상태 변경
      this._isOn = true;

      this.handler = new ScreenSpaceEventHandler(viewer.canvas); // 핸들러

      this._instantAlert = new DitapAlert({
        type: "info",
        title: "이동 편집",
        content1: "해당 객체는 절단면 기능을",
        content2: "지원하지 않습니다.",
        buttons: "ONLYOK",
        open: false,
      });

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭 또는 터치하여 모델을 선택해주세요"
      );
      // 설명 라벨 생성
      this.infoLabel = Utils.createInfoLabel(
        this.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭하여 모델을 선택해주세요"
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.createMobileEvent();
        this.infoLabel.style.display = "none";
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      // 핸들러 실행
      this.createLeftClickHandler();
      this.createRightClickHandler();
      this.createKeyDownHandler();
      this.createResizeEvent();
    }
  }

  /**
   * 절단면 분석 핸들러 제거.</br>
   * End InteractionClipping handler
   */
  off() {
    if (this._isOn) {
      // 버튼 비활성화
      this.buttonElement?.classList.remove("on");

      // 도움말 라벨 제거.
      this.mobileInfoLabel?.label.remove();
      this.infoLabel?.remove();

      // 이벤트 핸들러
      if (this.handler) {
        this.handler.destroy();
      }

      // 팝업창
      if (this.popupAnalysisClipping instanceof PopupAnalysisClipping) {
        // this.popupAnalysisClipping.destroy();
        this.popupAnalysisClipping = undefined;
      }

      window.removeEventListener("keydown", this.keyDownEvent);
      if (this._isMobile) {
        this.removeMobileEvent();
      }

      if (this.resizeEvent) {
        window.removeEventListener("resize", this.resizeEvent);
      }

      // 핸들러 상태 변경
      this._isOn = false;
      this._isMobile = undefined;
      this.selectObject = undefined;
      this.viewer.currentEvent = undefined;
      this.infoLabelEntity = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;
    }
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.off();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
    }
    this.cancelEvent = undefined;
    this.cancelBtn = undefined;
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;

            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();
          }
        }
      }
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.off();
  }
}

export default InteractionClipping;
